import React, { useMemo } from 'react';
import { Filter, Warehouse, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const WeeklyOrderTab = ({
  allItems,
  weeklySupplier,
  setWeeklySupplier,
  weeklyQuantities,
  setWeeklyQuantities,
  loading,
  refreshWeeklyStock
}) => {
  // Suppliers for weekly
  const weeklySuppliers = useMemo(() => {
    return [...new Set(allItems.map(item => item.supplier || 'Sin asignar'))].sort();
  }, [allItems]);

  // Filtered items for weekly
  const filteredWeeklyItems = useMemo(() => {
    if (weeklySupplier === 'all') return allItems;
    return allItems.filter(item => (item.supplier || 'Sin asignar') === weeklySupplier);
  }, [allItems, weeklySupplier]);

  const updateWeeklyQuantity = (itemId, value) => {
    setWeeklyQuantities(prev => ({
      ...prev,
      [itemId]: Math.max(0, parseInt(value) || 0)
    }));
  };

  const getWeeklyTotal = () => {
    return Object.values(weeklyQuantities).reduce((sum, qty) => sum + qty, 0);
  };

  const exportWeeklyOrder = () => {
    const itemsToExport = filteredWeeklyItems
      .filter(item => weeklyQuantities[item.id] > 0)
      .map(item => ({
        codigo: item.code,
        nombre: item.name,
        proveedor: item.supplier || 'Sin asignar',
        stock_bodega: item.current_stock,
        cantidad_pedido: weeklyQuantities[item.id],
        unidad: item.unit
      }));

    if (itemsToExport.length === 0) {
      toast.error('No hay productos con cantidad asignada');
      return;
    }

    const headers = ['Código', 'Nombre', 'Proveedor', 'Stock Bodega', 'Cantidad Pedido', 'Unidad'];
    const csv = [
      headers.join(','),
      ...itemsToExport.map(item => 
        `${item.codigo},${item.nombre},${item.proveedor},${item.stock_bodega},${item.cantidad_pedido},${item.unidad}`
      )
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pedido_semanal_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Pedido semanal exportado');
  };

  return (
    <div className="space-y-6" data-testid="weekly-order-tab">
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-end justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-1">Pedido Semanal</h2>
            <p className="text-sm text-muted-foreground">
              Revisa el stock y planifica tu pedido semanal a proveedores
            </p>
          </div>
          <div className="flex gap-3">
            <Button onClick={refreshWeeklyStock} variant="outline" disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualizar Stock
            </Button>
            <Select value={weeklySupplier} onValueChange={setWeeklySupplier}>
              <SelectTrigger className="w-52" data-testid="weekly-supplier-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrar por proveedor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los proveedores</SelectItem>
                {weeklySuppliers.map(supplier => (
                  <SelectItem key={supplier} value={supplier}>{supplier}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={exportWeeklyOrder} variant="outline" data-testid="export-csv-btn">
              Exportar CSV
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-sm text-muted-foreground">Productos</p>
          <p className="text-2xl font-bold text-slate-900">{filteredWeeklyItems.length}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-sm text-muted-foreground">Proveedores</p>
          <p className="text-2xl font-bold text-slate-900">{weeklySuppliers.length}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-sm text-muted-foreground">Items a Pedir</p>
          <p className="text-2xl font-bold text-primary">
            {Object.values(weeklyQuantities).filter(q => q > 0).length}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-sm text-muted-foreground">Total Unidades</p>
          <p className="text-2xl font-bold text-green-600">{getWeeklyTotal()}</p>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider sticky top-0">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Código</th>
              <th className="px-4 py-3 text-left font-semibold">Producto</th>
              <th className="px-4 py-3 text-left font-semibold">Proveedor</th>
              <th className="px-4 py-3 text-center font-semibold">
                <div className="flex items-center justify-center gap-1">
                  <Warehouse className="w-4 h-4" />
                  Stock Bodega
                </div>
              </th>
              <th className="px-4 py-3 text-center font-semibold">Unidad</th>
              <th className="px-4 py-3 text-center font-semibold bg-blue-50">Cantidad a Pedir</th>
            </tr>
          </thead>
          <tbody className="max-h-96 overflow-y-auto">
            {filteredWeeklyItems.map((item, index) => (
              <motion.tr
                key={item.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.02 }}
                className={`border-b hover:bg-slate-50 ${
                  item.current_stock <= item.minimum_stock ? 'bg-red-50/50' : ''
                }`}
              >
                <td className="px-4 py-3 font-mono text-sm">{item.code}</td>
                <td className="px-4 py-3">
                  <div>
                    <p className="font-medium text-slate-900">{item.name}</p>
                    {item.current_stock <= item.minimum_stock && (
                      <span className="text-xs text-red-600">Stock bajo</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-md">
                    {item.supplier || 'Sin asignar'}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`font-semibold ${
                    item.current_stock <= item.minimum_stock ? 'text-red-600' : 'text-slate-900'
                  }`}>
                    {item.current_stock}
                  </span>
                </td>
                <td className="px-4 py-3 text-center text-slate-600">{item.unit}</td>
                <td className="px-4 py-3 bg-blue-50/50">
                  <Input
                    type="number"
                    min="0"
                    value={weeklyQuantities[item.id] || ''}
                    onChange={(e) => updateWeeklyQuantity(item.id, e.target.value)}
                    placeholder="0"
                    className="w-24 mx-auto text-center"
                    data-testid={`weekly-quantity-${item.id}`}
                  />
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary Footer */}
      {getWeeklyTotal() > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-green-900">Resumen del Pedido Semanal</p>
              <p className="text-sm text-green-700">
                {Object.values(weeklyQuantities).filter(q => q > 0).length} productos seleccionados
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-green-700">{getWeeklyTotal()} unidades</p>
              <Button onClick={exportWeeklyOrder} size="sm" className="mt-2 bg-green-600 hover:bg-green-700">
                Exportar Pedido
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeeklyOrderTab;
